import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  MessageCircle,
  Send,
  CheckCircle2,
  HelpCircle,
  User,
  ArrowRight,
  ArrowLeft,
  Loader2,
  ExternalLink,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useToast } from "../context/ToastContext";
import { submitContactInquiry } from "../api/contactApi";
import { updateSEO } from "../utils/seo";
import "./Contact.css";

export default function Contact() {
  const { t, isRTL, language } = useLanguage();
  const { addToast } = useToast();

  const [subject, setSubject] = useState("general");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    updateSEO({
      title: t("contact.metaTitle"),
      description: isRTL
        ? "تواصل مع فريق خدمة عملاء وكونسيرج تيري لاند (Terry Land) للمساعدة في اختيار المقاسات، تتبع الشحنات، وتنسيق هدايا وتجهيزات العرائس."
        : "Contact the Terry Land luxury concierge team for size consultations, order tracking, and bespoke bridal gifting assistance.",
      canonical: "https://terryland.net/contact",
    });
  }, [t, language, isRTL]);

  const subjectOptions = [
    { id: "general", labelKey: "contact.subjGeneral" },
    { id: "order", labelKey: "contact.subjOrder" },
    { id: "size", labelKey: "contact.subjSize" },
    { id: "wholesale", labelKey: "contact.subjWholesale" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!name.trim()) {
      const err = t("contact.errName");
      setErrorMsg(err);
      addToast(err, "error");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim() || !emailRegex.test(email.trim())) {
      const err = t("contact.errEmail");
      setErrorMsg(err);
      addToast(err, "error");
      return;
    }

    if (message.trim().length < 10) {
      const err = t("contact.errMsg");
      setErrorMsg(err);
      addToast(err, "error");
      return;
    }

    setSubmitting(true);
    try {
      await submitContactInquiry({
        name: name.trim(),
        email: email.trim(),
        phoneNumber: phone.trim() || null,
        subject,
        message: message.trim(),
      });
      setSubmitted(true);
      addToast(t("contact.toastSuccess"), "success");
      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to send message. Please try again.";
      setErrorMsg(msg);
      addToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setErrorMsg("");
  };

  return (
    <article className="contact-page" dir={isRTL ? "rtl" : "ltr"}>
      {/* ── Breadcrumb Navigation ── */}
      <nav className="contact-breadcrumb" aria-label="Breadcrumb">
        <div className="container contact-breadcrumb__inner">
          <Link to="/" className="contact-breadcrumb__link">
            {t("contact.breadcrumbHome")}
          </Link>
          <span className="contact-breadcrumb__sep">/</span>
          <span className="contact-breadcrumb__current" aria-current="page">
            {t("contact.breadcrumbCurrent")}
          </span>
        </div>
      </nav>

      {/* ── Hero Header ── */}
      <header className="contact-hero">
        <div className="container contact-hero__inner">
          <div className="contact-eyebrow-pill">
            <span>{t("contact.heroBadge")}</span>
          </div>

          <h1 className="contact-hero__title">
            {t("contact.heroTitle")}
          </h1>

          <p className="contact-hero__sub">
            {t("contact.heroSubtitle")}
          </p>
        </div>
      </header>

      {/* ── Main Layout: Channels & Inquiry Form ── */}
      <div className="container contact-layout">
        {/* Left Column: Direct Concierge Channels */}
        <aside className="contact-channels">
          {/* Direct WhatsApp Concierge Card */}
          <div className="contact-card contact-card--whatsapp">
            <div className="contact-card__icon-wrap contact-card__icon-wrap--emerald">
              <MessageCircle size={22} />
            </div>
            <div className="contact-card__body">
              <span className="contact-card__label">{t("contact.whatsappTitle")}</span>
              <p className="contact-card__desc">{t("contact.whatsappDesc")}</p>
              <div className="contact-card__val" dir="ltr">
                {t("contact.whatsappNumber")}
              </div>
              <a
                href={`https://wa.me/${t("contact.whatsappNumber").replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-whatsapp-btn"
              >
                <span>{t("contact.whatsappAction")}</span>
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* Email Support Card */}
          <div className="contact-card">
            <div className="contact-card__icon-wrap">
              <Mail size={20} />
            </div>
            <div className="contact-card__body">
              <span className="contact-card__label">{t("contact.emailTitle")}</span>
              <a
                href={`mailto:${t("contact.emailVal")}`}
                className="contact-card__link"
                dir="ltr"
              >
                {t("contact.emailVal")}
              </a>
            </div>
          </div>

          {/* Direct Phone Support Card */}
          <div className="contact-card">
            <div className="contact-card__icon-wrap">
              <Phone size={20} />
            </div>
            <div className="contact-card__body">
              <span className="contact-card__label">{t("contact.phoneTitle")}</span>
              <a
                href={`tel:${t("contact.phoneVal").replace(/\s/g, "")}`}
                className="contact-card__link"
                dir="ltr"
              >
                {t("contact.phoneVal")}
              </a>
            </div>
          </div>

          {/* Operating Hours & Location Card */}
          <div className="contact-card contact-card--details">
            <div className="contact-detail-row">
              <div className="contact-detail-icon">
                <Clock size={18} />
              </div>
              <div>
                <span className="contact-card__label">{t("contact.hoursTitle")}</span>
                <p className="contact-card__info-text">{t("contact.hoursVal")}</p>
              </div>
            </div>

            <div className="contact-detail-divider" />

            <div className="contact-detail-row">
              <div className="contact-detail-icon">
                <MapPin size={18} />
              </div>
              <div>
                <span className="contact-card__label">{t("contact.locationTitle")}</span>
                <p className="contact-card__info-text">{t("contact.locationVal")}</p>
              </div>
            </div>
          </div>

          {/* Quick FAQ Link Box */}
          <div className="contact-faq-banner">
            <div className="contact-faq-banner__icon">
              <HelpCircle size={20} />
            </div>
            <div className="contact-faq-banner__text">
              <p>{t("contact.faqPrompt")}</p>
              <Link to="/faq" className="contact-faq-banner__link">
                <span>{t("contact.faqLink")}</span>
                {isRTL ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
              </Link>
            </div>
          </div>
        </aside>

        {/* Right Column: Inquiry Message Form */}
        <section className="contact-form-section">
          <div className="contact-form-card">
            {submitted ? (
              <div className="contact-success-state">
                <div className="contact-success-icon-wrap">
                  <CheckCircle2 size={42} />
                </div>
                <h2 className="contact-success-title">{t("contact.successTitle")}</h2>
                <p className="contact-success-desc">{t("contact.successDesc")}</p>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn-primary contact-send-another-btn"
                >
                  {t("contact.sendAnother")}
                </button>
              </div>
            ) : (
              <>
                <div className="contact-form-header">
                  <h2 className="contact-form-title">{t("contact.formTitle")}</h2>
                  <p className="contact-form-subtitle">{t("contact.formSubtitle")}</p>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  {errorMsg && (
                    <div className="contact-form-alert" role="alert">
                      {errorMsg}
                    </div>
                  )}

                  {/* Subject Pills */}
                  <div className="contact-field-group">
                    <label className="contact-field-label">
                      {t("contact.subjectLabel")}
                    </label>
                    <div className="contact-subject-pills">
                      {subjectOptions.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          className={`contact-subject-pill ${
                            subject === opt.id ? "contact-subject-pill--active" : ""
                          }`}
                          onClick={() => setSubject(opt.id)}
                        >
                          {t(opt.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Full Name Field */}
                  <div className="contact-field-group">
                    <label htmlFor="cf-name" className="contact-field-label">
                      {t("contact.nameLabel")} <span className="contact-req">*</span>
                    </label>
                    <div className="contact-input-wrap">
                      <span className="contact-input-icon">
                        <User size={18} />
                      </span>
                      <input
                        id="cf-name"
                        type="text"
                        className="contact-input"
                        placeholder={t("contact.namePlaceholder")}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  {/* Email & Phone Grid */}
                  <div className="contact-two-col">
                    <div className="contact-field-group">
                      <label htmlFor="cf-email" className="contact-field-label">
                        {t("contact.emailLabel")} <span className="contact-req">*</span>
                      </label>
                      <div className="contact-input-wrap">
                        <span className="contact-input-icon">
                          <Mail size={18} />
                        </span>
                        <input
                          id="cf-email"
                          type="email"
                          className="contact-input"
                          placeholder={t("contact.emailPlaceholder")}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          dir="ltr"
                          required
                        />
                      </div>
                    </div>

                    <div className="contact-field-group">
                      <label htmlFor="cf-phone" className="contact-field-label">
                        {t("contact.phoneLabel")}
                      </label>
                      <div className="contact-input-wrap">
                        <span className="contact-input-icon">
                          <Phone size={18} />
                        </span>
                        <input
                          id="cf-phone"
                          type="tel"
                          className="contact-input"
                          placeholder={t("contact.phonePlaceholder")}
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          dir="ltr"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Message Field */}
                  <div className="contact-field-group">
                    <div className="contact-msg-header">
                      <label htmlFor="cf-msg" className="contact-field-label">
                        {t("contact.msgLabel")} <span className="contact-req">*</span>
                      </label>
                      <span
                        className={`contact-char-count ${
                          message.trim().length >= 10 ? "contact-char-count--valid" : ""
                        }`}
                      >
                        {t("contact.charCount", { count: message.length })}
                      </span>
                    </div>
                    <textarea
                      id="cf-msg"
                      rows={5}
                      maxLength={500}
                      className="contact-textarea"
                      placeholder={t("contact.msgPlaceholder")}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </div>

                  {/* Submit CTA */}
                  <button
                    type="submit"
                    className="btn-primary contact-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="spinner" />
                        <span>{t("contact.sending")}</span>
                      </>
                    ) : (
                      <>
                        <Send size={16} />
                        <span>{t("contact.btnSend")}</span>
                        {isRTL ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </section>
      </div>
    </article>
  );
}
